"use client";

/**
 * A release's Credits tab as liner notes (Gordon 2026-11-03: "all the tracks
 * and credits should be listed here … like a real album credit listing all
 * assembled here as well as individual tracks. make sure the titles are also
 * linked"). Whole album first — every role with who did it and on which
 * tracks — then track by track, each title linking to its page.
 */

import { Fragment, useMemo } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { CreditLink } from "@/components/credits/credit-link";
import { assembleAlbumCredits, byRole, cleanTrackTitle, trackRanges, type SheetCredit, type SheetLine, type SheetTrack } from "@/lib/album-credit-sheet";

function People({ line, showTracks }: { line: SheetLine; showTracks: boolean }) {
  const t = useTranslations("albumCredits");
  return (
    <>
      {line.people.map((p, i) => (
        <Fragment key={`${p.href || ""}:${p.name}`}>
          {i > 0 && ", "}
          <CreditLink name={p.name} href={p.href} role={line.role} />
          {showTracks && p.tracks.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground"> ({t(p.tracks.length === 1 ? "trackOne" : "trackMany", { list: trackRanges(p.tracks) })})</span>
          )}
        </Fragment>
      ))}
    </>
  );
}

export function AlbumCreditSheet({ album, tracks }: { album: SheetCredit[]; tracks: SheetTrack[] }) {
  const t = useTranslations("albumCredits");
  const lines = useMemo(() => assembleAlbumCredits(album, tracks), [album, tracks]);
  if (lines.length === 0 && tracks.length === 0) return <p className="py-4 text-center text-sm text-muted-foreground">{t("empty")}</p>;
  return (
    <div className="space-y-6">
      <section aria-labelledby="album-credits-whole">
        <h3 id="album-credits-whole" className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("whole")}</h3>
        {lines.length > 0 ? (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {lines.map(line => (
              <div key={line.role} className="text-sm">
                <dt className="inline text-muted-foreground">{line.role}: </dt>
                <dd className="inline font-medium"><People line={line} showTracks /></dd>
              </div>
            ))}
          </dl>
        ) : <p className="text-sm text-muted-foreground">{t("empty")}</p>}
      </section>

      {tracks.length > 0 && (
        <section aria-labelledby="album-credits-tracks">
          <h3 id="album-credits-tracks" className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("byTrack")}</h3>
          <ol className="divide-y divide-border/50">
            {tracks.map(tr => {
              const roles = byRole(tr.credits);
              return (
                <li key={`${tr.n}:${tr.href}`} className="py-2.5">
                  <div className="flex items-baseline gap-2">
                    <span className="w-6 shrink-0 text-right text-sm tabular-nums text-muted-foreground">{tr.n}</span>
                    <Link href={tr.href} className="min-w-0 font-semibold hover:underline">{cleanTrackTitle(tr.title, tr.n)}</Link>
                  </div>
                  {roles.length > 0 ? (
                    <dl className="mt-1 space-y-0.5 pl-8 text-sm">
                      {roles.map(line => (
                        <div key={line.role}>
                          <dt className="inline text-muted-foreground">{line.role}: </dt>
                          <dd className="inline font-medium"><People line={line} showTracks={false} /></dd>
                        </div>
                      ))}
                    </dl>
                  ) : <p className="mt-0.5 pl-8 text-xs text-muted-foreground">{t("none")}</p>}
                </li>
              );
            })}
          </ol>
        </section>
      )}
    </div>
  );
}
